/**
 * The icon vocabulary.
 *
 * Inline SVG rather than Unicode characters, deliberately. The two glyphs the app already used —
 * `◷` for "find a slot" and `⠿` for the drag grip — happen to render on this machine, but
 * coverage for less common characters varies by platform and a missing one renders as a tofu box.
 * A sprite of hand-written paths is a few hundred bytes and cannot fail.
 *
 * Every icon here replaced a text label that passed three tests: its set of possible values is
 * closed and small, it repeats on many rows at once, and it names a category rather than a
 * quantity. Anything failing one of those stayed text — which is why there is no icon for a goal
 * name (no fixed set), for `90m` (a quantity is already the shortest form of itself), or for the
 * board's column headings (shown once, and they are what teach the vocabulary these are shorthand
 * for).
 *
 * Shape carries the meaning, never colour. A blocked hexagon and an overdue triangle differ as
 * outlines, because roughly one man in twelve will not reliably separate them by hue — the same
 * rule the goal palette follows.
 *
 * Icons are decorative by default (`aria-hidden`): the *control* carries the accessible name and
 * the `title`, so a screen reader hears "Start Gym" rather than "Start Gym, triangle". Pass
 * `label` only for an icon standing alone with no labelled parent.
 */

import type { ReactElement } from 'react';

export type IconName =
  | 'start'
  | 'more'
  | 'overdue'
  | 'blocked'
  | 'slot'
  | 'block-time'
  | 'reminder'
  | 'recur'
  | 'ready'
  | 'no-next'
  | 'next'
  | 'inbox'
  | 'drop'
  | 'park'
  | 'done'
  | 'caret'
  | 'folder'
  | 'goal'
  | 'search'
  | 'plus'
  | 'edit'
  | 'bell'
  | 'trash'
  | 'open'
  | 'subtask-of';

/* 16×16, 1.5 stroke, `currentColor` throughout so tone comes from the pill class. */
const PATHS: Record<IconName, ReactElement> = {
  // A play triangle: begin this.
  start: <path d="M5 3.5l7 4.5-7 4.5V3.5z" fill="currentColor" stroke="none" />,
  // Three dots: everything else this row can do.
  more: (
    <g fill="currentColor" stroke="none">
      <circle cx="3.5" cy="8" r="1.3" />
      <circle cx="8" cy="8" r="1.3" />
      <circle cx="12.5" cy="8" r="1.3" />
    </g>
  ),
  // Warning triangle: the date has passed.
  overdue: (
    <g>
      <path d="M8 2.2l6 11.6H2L8 2.2z" />
      <path d="M8 6.4v3.2" strokeLinecap="round" />
      <circle cx="8" cy="11.6" r="0.85" fill="currentColor" stroke="none" />
    </g>
  ),
  // Hexagon: blocked by another task. A different outline from the triangle, on purpose.
  blocked: (
    <g>
      <path d="M8 1.8l5.4 3.1v6.2L8 14.2l-5.4-3.1V4.9L8 1.8z" />
      <path d="M8 5.6v3.1" strokeLinecap="round" />
      <circle cx="8" cy="10.9" r="0.85" fill="currentColor" stroke="none" />
    </g>
  ),
  // A clock face, open at the top right: you will pick a time later.
  slot: (
    <g>
      <circle cx="8" cy="8" r="5.9" />
      <path d="M8 4.6V8l2.6 1.6" strokeLinecap="round" />
    </g>
  ),
  // A framed block: the time is already taken.
  'block-time': (
    <g>
      <rect x="2.2" y="2.9" width="11.6" height="10.2" rx="1.4" />
      <rect x="4.6" y="6.2" width="6.8" height="4" rx="0.6" fill="currentColor" stroke="none" />
    </g>
  ),
  // A bell.
  reminder: (
    <g>
      <path d="M4.4 11.2V7.6a3.6 3.6 0 017.2 0v3.6l1.2 1.4H3.2l1.2-1.4z" />
      <path d="M6.7 13.9a1.5 1.5 0 002.6 0" strokeLinecap="round" />
    </g>
  ),
  // A closing loop: this one comes back.
  recur: (
    <g>
      <path d="M13.2 8a5.2 5.2 0 01-9.1 3.4" strokeLinecap="round" />
      <path d="M2.8 8a5.2 5.2 0 019.1-3.4" strokeLinecap="round" />
      <path d="M11.4 1.9v2.9h-2.9" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4.6 14.1v-2.9h2.9" strokeLinecap="round" strokeLinejoin="round" />
    </g>
  ),
  // A filled ring: everything under this goal is done but the goal itself.
  ready: (
    <g>
      <circle cx="8" cy="8" r="6" />
      <circle cx="8" cy="8" r="2.9" fill="currentColor" stroke="none" />
    </g>
  ),
  // An empty ring: nothing is queued under this goal.
  'no-next': <circle cx="8" cy="8" r="6" />,
  // A chevron: promote this to next.
  next: <path d="M6 4l4.2 4L6 12" strokeLinecap="round" strokeLinejoin="round" />,
  // A tray: back to undecided.
  inbox: (
    <g>
      <path d="M2.2 8.6h3.1l1 1.7h3.4l1-1.7h3.1" strokeLinejoin="round" />
      <path
        d="M2.2 8.6l2-5.4h7.6l2 5.4v3.5a1 1 0 01-1 1H3.2a1 1 0 01-1-1V8.6z"
        strokeLinejoin="round"
      />
    </g>
  ),
  // A struck circle: not doing this.
  drop: (
    <g>
      <circle cx="8" cy="8" r="6" />
      <path d="M4.4 11.6l7.2-7.2" strokeLinecap="round" />
    </g>
  ),
  // Two upright bars: put down, not struck out. Deliberately not a variant of `drop`'s struck
  // circle — parking and dropping are the two things a reader most needs to tell apart here, and
  // `taskVocab.spec.ts` asserts no two statuses share a glyph for exactly that reason.
  park: (
    <g strokeLinecap="round">
      <path d="M6.2 4.2v7.6" />
      <path d="M9.8 4.2v7.6" />
    </g>
  ),
  // A tick.
  done: <path d="M3.4 8.4l3.1 3.1 6.1-6.9" strokeLinecap="round" strokeLinejoin="round" />,
  // A disclosure chevron, for anything that opens downward.
  caret: <path d="M4 6.5l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />,
  // A folder: a project.
  folder: (
    <path
      d="M2 4.5a1 1 0 011-1h3l1.4 1.6H13a1 1 0 011 1v6.4a1 1 0 01-1 1H3a1 1 0 01-1-1V4.5z"
      strokeLinejoin="round"
    />
  ),
  // Concentric rings: a goal.
  goal: (
    <g>
      <circle cx="8" cy="8" r="6" />
      <circle cx="8" cy="8" r="2.6" />
    </g>
  ),
  // A plus: capture this.
  plus: <path d="M8 3.4v9.2M3.4 8h9.2" strokeLinecap="round" strokeWidth="1.7" />,
  /*
    Up and out of a branch: the reader is standing on the subtask and the parent is above it. An
    L rather than a bare arrow, because the corner is what says "out of here" rather than "move
    up one place in this list" — this sits on rows that also answer to `j` and `k`.
  */
  'subtask-of': (
    <path
      d="M4 12.6V6.4a1.6 1.6 0 011.6-1.6h6.2M9.2 2.2L11.8 4.8 9.2 7.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  /*
    A bell. It was the `🔔` emoji, which is precisely the failure this file's docstring describes:
    it rendered as a tofu box in the test browser, so the notifications control was an empty
    rectangle in the header. The header now has four controls instead of twelve, which is how it
    became obvious.
  */
  /*
    A bin, for Delete — which used to borrow `drop`'s circle-and-slash.

    Drop and Delete sat one above the other in the task menu wearing the same glyph, and they are
    not the same thing at all: dropping a task decides not to do it and keeps the record, deleting
    it removes the record. One glyph, one meaning, is the rule this file opens with.
  */
  trash: (
    <g strokeLinecap="round" strokeLinejoin="round">
      <path d="M3.2 4.5h9.6M6.4 4.5V3.2h3.2v1.3" />
      <path d="M4.4 4.5l.6 8.3h6l.6-8.3" />
      <path d="M6.7 6.9v3.6M9.3 6.9v3.6" />
    </g>
  ),
  /* An arrow leaving a box: open this task's own panel. */
  open: (
    <g strokeLinecap="round" strokeLinejoin="round">
      <path d="M8.4 2.8H3.4v9.8h9.8V7.6" />
      <path d="M13.2 2.8h-3.6M13.2 2.8v3.6M13.2 2.8L7.9 8.1" />
    </g>
  ),
  bell: (
    <g strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 11.4V7.3a4 4 0 018 0v4.1l1 1.4H3z" />
      <path d="M6.6 14.1a1.6 1.6 0 002.8 0" />
    </g>
  ),
  // A pencil: the one verb in the bar that opens something rather than doing something.
  edit: (
    <g strokeLinecap="round" strokeLinejoin="round">
      <path d="M11.1 2.6l2.3 2.3-7.4 7.4-3 .7.7-3z" />
      <path d="M9.6 4.1l2.3 2.3" />
    </g>
  ),
  search: (
    <g>
      <circle cx="7.2" cy="7.2" r="4.6" />
      <path d="M10.6 10.6l3 3" strokeLinecap="round" />
    </g>
  ),
};

export function Icon({
  name,
  size = 16,
  label,
}: {
  name: IconName;
  size?: number;
  /** Only for an icon with no labelled parent. Otherwise the control owns the name. */
  label?: string;
}) {
  return (
    <svg
      className="icon"
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}
